/**
 * Query keys do domínio students (apps/crm).
 *
 * `viewScope` participa da key porque a lista de alunos muda entre
 * `'club'` (apenas o clube atual) e `'network'` (multi-clube via
 * `networkClubIds`) — sem isso, alternar o toggle não invalida.
 */
import { tuple } from "./core";

const root = ["students"] as const;

export type StudentViewScope = "club" | "network";

export const students = {
  all: root,
  /** Lista de alunos do clube/rede atual. */
  list: (
    clubId: string | null | undefined,
    viewScope: StudentViewScope | { type: StudentViewScope } | null | undefined,
  ) => tuple(root, "list", clubId, viewScope),
  byId: (studentId: string | null | undefined) => tuple(root, "detail", studentId),
  /** Faturas mensais de um aluno (`student_monthly_billing`). */
  billings: (studentId: string | null | undefined) =>
    tuple(root, "billings", studentId),
  /**
   * TODAS as cobranças de um aluno: as mensalidades de
   * `student_monthly_billing` unidas às faturas de `student_invoices`
   * (evento, locação, fidelidade e aula avulsa sem plano), com dedupe por
   * `student_invoices.monthly_billing_id`.
   *
   * Key própria, e não `billings`, porque a leitura é OUTRA (duas tabelas) —
   * reaproveitar a key faria uma das duas telas ler o cache da outra e perder
   * metade das linhas. Fica sob o root `students` de propósito: markPaid de
   * fatura, baixa de mensalidade e reemissão já invalidam `qk.students.all`,
   * e o prefix-match cobre esta key sem que ninguém precise lembrar dela.
   */
  charges: (studentId: string | null | undefined) =>
    tuple(root, "charges", studentId),
  /**
   * Prévia de identidade da conversão (`convert-lead` com `previa_identidade`):
   * de quem é a conta que o contato do lead aponta, para o nome digitado. Sob o
   * root `students` de propósito: converter invalida `qk.students.all` e a
   * próxima prévia já enxerga o aluno recém-criado.
   */
  previaDeIdentidade: (
    clubId: string | null | undefined,
    leadId: string | null | undefined,
    nome: string,
  ) => tuple(root, "previa-identidade", clubId, leadId, nome),
  /** Jornada (lessons + journey_status) de um aluno. */
  journey: (studentId: string | null | undefined) =>
    tuple(root, "journey", studentId),
  /**
   * Quais destes alunos são AVULSOS (toda matrícula ativa em plano
   * `per_attendance`) — selo "Avulso" da lista e da ficha (23/10/2026).
   * Sob o root `students`: cadastro e matrícula já invalidam `qk.students.all`.
   */
  avulsos: (studentIds: readonly string[]) =>
    tuple(root, "avulsos", [...studentIds].sort().join(",")),
  /**
   * Aulas em que o aluno esta matriculado (join club_lesson_students x
   * club_lessons x teacher), com filtros opcionais por periodo/status.
   * Wave 3 — Alunos UX: tab "Aulas" no detalhe do aluno.
   */
  lessons: (
    studentId: string | null | undefined,
    filters?: Record<string, unknown>,
  ) => tuple(root, "lessons", studentId, filters ?? {}),
  /**
   * Pacotes de créditos (student_packages) de um aluno — FASE P3 (PPS para
   * Professores). Historico completo, ordenado por purchased_at DESC.
   */
  packages: (studentId: string | null | undefined) =>
    tuple(root, "packages", studentId),
  /**
   * Linha do tempo de evolução (student_progress_entries) de um aluno —
   * FASE P5.1 (PPS para Professores).
   */
  progress: (studentId: string | null | undefined) =>
    tuple(root, "progress", studentId),
  /**
   * Movimento de alunos (28/10/2026): entradas, saídas e terminando, pelas
   * RPCs `movimento_*`. Sob o root `students`: cadastro, matrícula e
   * encerramento já invalidam `qk.students.all`, e o prefix-match cobre.
   */
  movimento: (
    aba: "entradas" | "saidas" | "terminando" | "trocas",
    filtros: Record<string, unknown>,
  ) => tuple(root, "movimento", aba, filtros),
} as const;
